import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api, { apiError, API } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const PALETTE = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#14B8A6"];
const RANGES = [{ v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }, { v: 365, l: "1y" }];
const STATUSES = ["all", "active", "trial", "past_due", "canceled", "suspended"];
const PLANS = ["all", "basic", "advanced", "professional"];
const TABS = [{ k: "business", l: "Business & Subscriptions", i: "TrendingUp" }, { k: "qr", l: "QR & Scans", i: "QrCode" }, { k: "usage", l: "Usage & Health", i: "Activity" }];

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [tab, setTab] = useState("business");
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    setD(null);
    api.get("/admin/analytics", { params: { days, plan, status } })
      .then(({ data }) => setD(data))
      .catch((err) => { if (err.response?.status === 403) setForbidden(true); else toast.error(apiError(err.response?.data?.detail)); });
  }, [days, plan, status]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const q = new URLSearchParams({ days, plan, status }).toString();
    window.open(`${API}/admin/analytics/export?${q}`, "_blank");
  };

  return (
    <AdminLayout title="Analytics" subtitle="Real-time customer, revenue, QR and platform insights"
      actions={<button data-testid="analytics-export" onClick={exportCsv} className="rounded-xl border border-border px-3 py-2 text-sm font-semibold hover:bg-accent flex items-center gap-1.5"><Icon name="Download" className="h-4 w-4" />Export CSV</button>}>
      {forbidden ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 px-4 py-3 text-sm">You need billing access to view analytics.</div>
      ) : (
        <div className="space-y-5" data-testid="admin-analytics">
          {/* filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl border border-border overflow-hidden">
              {RANGES.map((r) => <button key={r.v} data-testid={`range-${r.l}`} onClick={() => setDays(r.v)} className={`px-3 py-1.5 text-sm font-semibold ${days === r.v ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{r.l}</button>)}
            </div>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="filter-plan" className="rounded-xl border border-input bg-background px-3 py-1.5 text-sm capitalize">{PLANS.map((p) => <option key={p} value={p}>{p === "all" ? "All plans" : p}</option>)}</select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="rounded-xl border border-input bg-background px-3 py-1.5 text-sm capitalize">{STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}</select>
          </div>

          <div className="flex flex-wrap gap-2 border-b border-border">
            {TABS.map((t) => <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}><Icon name={t.i} className="h-4 w-4" />{t.l}</button>)}
          </div>

          {!d ? <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div> : (
            <>
              {tab === "business" && <Business d={d} />}
              {tab === "qr" && <QR d={d} />}
              {tab === "usage" && <Usage d={d} />}
            </>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

const Stat = ({ label, value, sub, icon, tone = "primary" }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={`stat-${(label || "").replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground">{label}</div>{icon && <Icon name={icon} className={`h-4 w-4 text-${tone}`} />}</div>
    <div className="text-2xl font-extrabold mt-1">{value}</div>
    {sub != null && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}><h3 className="text-sm font-bold mb-3">{title}</h3>{children}</div>
);

const axis = { fontSize: 11, stroke: "hsl(var(--muted-foreground))" };
const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12, color: "hsl(var(--foreground))" }, itemStyle: { color: "hsl(var(--foreground))" }, labelStyle: { color: "hsl(var(--foreground))" } };

function Business({ d }) {
  const b = d.business;
  const dist = Object.entries(b.plan_distribution || {}).map(([name, value]) => ({ name, value }));
  const statuses = Object.entries(b.status_counts || {}).map(([name, value]) => ({ name, value }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total customers" value={b.total_customers} icon="Users" />
        <Stat label="Active customers" value={b.active_customers} sub={`${b.new_signups_30d} new / 30d`} icon="UserCheck" tone="emerald-500" />
        {d.show_revenue && <Stat label="MRR" value={`$${b.mrr}`} sub={`ARR $${b.arr}`} icon="DollarSign" tone="emerald-500" />}
        {d.show_revenue && <Stat label="ARPU" value={`$${b.arpu}`} sub="per paying customer" icon="TrendingUp" />}
        <Stat label="Trial conversion" value={`${b.trial_conversion}%`} icon="Percent" />
        <Stat label="Churn rate" value={`${b.churn}%`} icon="TrendingDown" tone="red-500" />
        <Stat label="Failed payments" value={b.failed_payments} sub={`${b.recovery_rate}% recovered`} icon="AlertTriangle" tone="amber-500" />
        <Stat label="Coupons redeemed" value={b.coupons.redeemed} sub={`${b.coupons.active} active`} icon="Ticket" />
      </div>

      <Card title="New signups over time" testid="chart-signups">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={b.signups_series}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={axis} minTickGap={24} /><YAxis tick={axis} allowDecimals={false} /><Tooltip {...tip} /><Line type="monotone" dataKey="count" name="Signups" stroke={PALETTE[0]} strokeWidth={2} dot={false} /></LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Plan distribution" testid="chart-plan-dist">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart><Pie data={dist} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{dist.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><Tooltip {...tip} /><Legend /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Subscription status" testid="chart-status">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statuses}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" tick={axis} /><YAxis tick={axis} allowDecimals={false} /><Tooltip {...tip} /><Bar dataKey="value" name="Customers" radius={[6, 6, 0, 0]}>{statuses.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {d.show_revenue && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Revenue by plan" testid="chart-rev-plan"><TopList items={Object.entries(b.revenue_by_plan || {}).map(([name, value]) => ({ name, value: `$${value}` }))} /></Card>
          <Card title="Revenue by country" testid="chart-rev-country"><TopList items={(b.revenue_by_country || []).map((x) => ({ name: x.name, value: `$${x.value}` }))} /></Card>
        </div>
      )}
      <Card title="Customers by country" testid="chart-country"><TopList items={b.country_distribution} /></Card>
    </div>
  );
}

function QR({ d }) {
  const q = d.qr;
  const grow = q.scan_growth;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total QR codes" value={q.total_qrs} sub={`${q.published} live · ${q.paused} paused`} icon="QrCode" />
        <Stat label="Total scans" value={q.total_scans} sub={`${q.unique_scans} unique · ${q.repeat_scans} repeat`} icon="ScanLine" />
        <Stat label="Scan growth" value={`${grow > 0 ? "+" : ""}${grow}%`} sub={`vs prev ${d.range.days}d (${q.prev_scans})`} icon={grow >= 0 ? "TrendingUp" : "TrendingDown"} tone={grow >= 0 ? "emerald-500" : "red-500"} />
        <Stat label="Zero-scan QRs" value={q.zero_scan} sub={`${q.archived} archived`} icon="EyeOff" tone="amber-500" />
      </div>

      <Card title="Scans over time" testid="chart-scans">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={q.scan_series}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={axis} minTickGap={24} /><YAxis tick={axis} allowDecimals={false} /><Tooltip {...tip} /><Legend /><Line type="monotone" dataKey="scans" stroke={PALETTE[0]} strokeWidth={2} dot={false} /><Line type="monotone" dataKey="unique" stroke={PALETTE[1]} strokeWidth={2} dot={false} /></LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="QR codes by type" testid="chart-type">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart layout="vertical" data={q.by_type}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis type="number" tick={axis} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={axis} width={90} /><Tooltip {...tip} /><Bar dataKey="value" name="QRs" radius={[0, 6, 6, 0]} fill={PALETTE[4]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Static vs Dynamic" testid="chart-static-dynamic">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart><Pie data={[{ name: "Static", value: q.static_vs_dynamic.static }, { name: "Dynamic", value: q.static_vs_dynamic.dynamic }]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label><Cell fill={PALETTE[2]} /><Cell fill={PALETTE[0]} /></Pie><Tooltip {...tip} /><Legend /></PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card title="By device" testid="chart-device"><TopList items={q.by_device} /></Card>
        <Card title="By OS" testid="chart-os"><TopList items={q.by_os} /></Card>
        <Card title="By browser" testid="chart-browser"><TopList items={q.by_browser} /></Card>
        <Card title="By country" testid="chart-scan-country"><TopList items={q.by_country} /></Card>
        <Card title="By city" testid="chart-city"><TopList items={q.by_city} /></Card>
        <Card title="Top QR codes by scans" testid="top-qrs"><TopList items={q.top_qrs.map((x) => ({ name: x.name, value: x.scans }))} /></Card>
      </div>
    </div>
  );
}

function Usage({ d }) {
  const u = d.usage;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="DAU" value={u.dau} sub="active today" icon="Activity" />
        <Stat label="WAU" value={u.wau} sub="active this week" icon="Activity" />
        <Stat label="MAU" value={u.mau} sub="active this month" icon="Activity" />
        <Stat label="Open tickets" value={u.tickets.open} sub={`${u.tickets.resolved} resolved`} icon="LifeBuoy" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Most active customers (by scans)" testid="most-active">
          {u.most_active.length === 0 ? <p className="text-sm text-muted-foreground">No scan activity in range.</p> : <TopList items={u.most_active.map((x) => ({ name: x.name, value: x.scans }))} />}
        </Card>
        <Card title="Customers near their QR limit" testid="near-limit">
          {u.near_limit.length === 0 ? <p className="text-sm text-muted-foreground">No customers close to a limit.</p> : (
            <div className="space-y-2">{u.near_limit.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm"><span className="truncate font-medium">{c.name} <span className="text-xs text-muted-foreground capitalize">· {c.plan}</span></span><span className="text-xs font-mono">{c.used}/{c.limit}</span></div>
            ))}</div>
          )}
        </Card>
      </div>
    </div>
  );
}

const TopList = ({ items }) => (
  !items || items.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> : (
    <div className="space-y-2">
      {items.map((x, i) => {
        const max = Math.max(...items.map((y) => (typeof y.value === "number" ? y.value : parseFloat(String(y.value).replace(/[^0-9.]/g, "")) || 0)), 1);
        const num = typeof x.value === "number" ? x.value : parseFloat(String(x.value).replace(/[^0-9.]/g, "")) || 0;
        return (
          <div key={i} className="text-sm">
            <div className="flex items-center justify-between mb-0.5"><span className="truncate">{x.name}</span><span className="font-semibold text-xs ml-2">{x.value}</span></div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(num / max) * 100}%`, background: PALETTE[i % PALETTE.length] }} /></div>
          </div>
        );
      })}
    </div>
  )
);
